import { useState } from 'react';
import { MapPin } from 'lucide-react';
import {
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  NativeSelect,
  Skeleton,
  SkeletonText,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
  formatNumber,
  formatPercent,
} from '@mgdlead/ui';

import { useWorkspace } from '../workspace/WorkspaceContext';
import { humanizeError } from '../../lib/errors';
import { useCampaigns, useGroups, type PeriodArg } from '../../lib/api';
import { ESTADOS, MAPA_ALTURA, MAPA_LARGURA } from './mapa-brasil';
import {
  useRegiaoDoPublico,
  type RecorteRegiao,
  type RegiaoData,
  type Situacao,
} from './useRegiao';

/**
 * "De onde é o seu público" — mapa de calor do Brasil pelo DDD (pedido do dono, 11/10).
 *
 * O mapa é o contorno REAL dos estados (pedido do dono, 11/10 — a grade de quadrados não
 * "parecia o Brasil"). O preço do contorno é que DF, SE, AL, PB, RN, ES e RJ ficam minúsculos
 * para o dedo: por isso eles também viram botões com nome logo abaixo do mapa.
 *
 * Tudo que é número chega pronto de `analytics.regiao` (invariante 3): pessoas, %, intensidade
 * e posição. A tela escolhe cor, ordem de leitura e o que dizer quando não sabe.
 */

/** UFs pequenas demais para rótulo e toque dentro do contorno — ganham atalho embaixo. */
const PEQUENAS = ['DF', 'RN', 'PB', 'AL', 'SE', 'ES', 'RJ'] as const;

/** A rampa do design system (`--color-ramp-0..5`) e a tinta de texto que lê sobre cada passo. */
const NIVEL: Record<number, string> = {
  0: 'bg-ramp-0 text-muted-foreground',
  1: 'bg-ramp-1 text-ramp-ink-light',
  2: 'bg-ramp-2 text-ramp-ink-light',
  3: 'bg-ramp-3 text-ramp-ink-dark',
  4: 'bg-ramp-4 text-ramp-ink-dark',
  5: 'bg-ramp-5 text-ramp-ink-dark',
};

/** O mesmo passo da rampa em preenchimento de SVG, com a tinta do rótulo por cima. */
const NIVEL_FILL: Record<number, string> = {
  0: 'fill-ramp-0',
  1: 'fill-ramp-1',
  2: 'fill-ramp-2',
  3: 'fill-ramp-3',
  4: 'fill-ramp-4',
  5: 'fill-ramp-5',
};
const NIVEL_TINTA: Record<number, string> = {
  0: 'fill-muted-foreground',
  1: 'fill-ramp-ink-light',
  2: 'fill-ramp-ink-light',
  3: 'fill-ramp-ink-dark',
  4: 'fill-ramp-ink-dark',
  5: 'fill-ramp-ink-dark',
};

const n = (v: number) => formatNumber(v, { abbreviate: 'never' });

/** `<1%` em vez de `0%` quando há gente: zero aqui afirmaria "ninguém", e não é verdade. */
function pctTexto(pct: number | null, pessoas: number): string {
  if (pct === null) return '—';
  if (pessoas > 0 && pct < 0.01) return '<1%';
  return formatPercent(pct, 0);
}

export type RegiaoDoPublicoProps = {
  /** Período da tela que abriga o card — usado só em "Entraram no período". */
  periodo: PeriodArg;
  /** Rótulo curto do período ("Hoje", "30 dias"). */
  rotuloPeriodo: string;
  /**
   * Trava o card num grupo (para a página do grupo encaixar depois). Sem isto, o card mostra o
   * seletor de campanha/grupo.
   */
  groupId?: string;
};

export function RegiaoDoPublico({ periodo, rotuloPeriodo, groupId }: RegiaoDoPublicoProps) {
  const { workspace } = useWorkspace();
  const [situacao, setSituacao] = useState<Situacao>('NO_GRUPO');
  const [recorte, setRecorte] = useState<RecorteRegiao>(
    groupId ? { kind: 'GRUPO', id: groupId } : { kind: 'TODOS' },
  );
  const query = useRegiaoDoPublico(workspace.id, periodo, situacao, recorte);

  return (
    <Card>
      <CardHeader
        title="De onde é o seu público"
        description="Pelo DDD do telefone de cada pessoa."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Tabs
          variant="segmented"
          value={situacao}
          onValueChange={(v) => setSituacao(v === 'ENTRARAM_NO_PERIODO' ? v : 'NO_GRUPO')}
        >
          <TabsList label="Quem entra na conta">
            <TabsTrigger value="NO_GRUPO">Nos grupos agora</TabsTrigger>
            <TabsTrigger value="ENTRARAM_NO_PERIODO">Entraram · {rotuloPeriodo}</TabsTrigger>
          </TabsList>
        </Tabs>
        {groupId ? null : <SeletorDeRecorte value={recorte} onChange={setRecorte} />}
      </div>

      {query.isError ? (
        <ErrorState
          title="Não foi possível montar o mapa"
          description={humanizeError(query.error)}
          onRetry={() => void query.refetch()}
        />
      ) : query.isPending ? (
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="aspect-square w-full max-w-sm" />
          <SkeletonText lines={6} />
        </div>
      ) : query.data ? (
        <RegiaoConteudo data={query.data} />
      ) : null}
    </Card>
  );
}

function SeletorDeRecorte({
  value,
  onChange,
}: {
  value: RecorteRegiao;
  onChange: (r: RecorteRegiao) => void;
}) {
  const { workspace } = useWorkspace();
  const grupos = useGroups(workspace.id);
  const campanhas = useCampaigns(workspace.id);
  const listaCampanhas = campanhas.data?.campaigns ?? [];
  const listaGrupos = grupos.data?.groups ?? [];
  const atual =
    value.kind === 'TODOS' ? 'todos' : `${value.kind === 'CAMPANHA' ? 'c' : 'g'}:${value.id}`;

  return (
    <NativeSelect
      size="sm"
      aria-label="Ver por campanha ou grupo"
      wrapperClassName="min-w-0 flex-1 sm:max-w-64"
      value={atual}
      onChange={(e) => {
        const v = e.target.value;
        if (v.startsWith('c:')) onChange({ kind: 'CAMPANHA', id: v.slice(2) });
        else if (v.startsWith('g:')) onChange({ kind: 'GRUPO', id: v.slice(2) });
        else onChange({ kind: 'TODOS' });
      }}
    >
      <option value="todos">Todos os grupos</option>
      {listaCampanhas.length > 0 ? (
        <optgroup label="Campanhas">
          {listaCampanhas.map((c) => (
            <option key={c.id} value={`c:${c.id}`}>
              {c.name}
            </option>
          ))}
        </optgroup>
      ) : null}
      {listaGrupos.length > 0 ? (
        <optgroup label="Grupos">
          {listaGrupos.map((g) => (
            <option key={g.id} value={`g:${g.id}`}>
              {g.name ?? 'Grupo sem nome'}
            </option>
          ))}
        </optgroup>
      ) : null}
    </NativeSelect>
  );
}

/** O miolo do card, sem rede — é o que os testes renderizam direto. */
export function RegiaoConteudo({ data }: { data: RegiaoData }) {
  const { cobertura } = data;
  const lider = data.porUf.find((u) => u.posicao === 1) ?? null;
  const [ufEscolhida, setUfEscolhida] = useState<string | null>(null);
  const uf = data.porUf.find((u) => u.uf === (ufEscolhida ?? lider?.uf)) ?? null;

  if (cobertura.total === 0) {
    return (
      <EmptyState
        kind="no-data"
        icon={MapPin}
        title="Ninguém neste recorte ainda"
        description={
          data.situacao === 'NO_GRUPO'
            ? 'Assim que os grupos tiverem gente, mostramos de onde ela é.'
            : 'Ninguém entrou neste período. Troque o período ou veja quem está nos grupos agora.'
        }
        compact
      />
    );
  }

  if (cobertura.comTelefoneBr === 0) {
    return (
      <>
        <EmptyState
          kind="no-data"
          icon={MapPin}
          title="Ainda não sabemos o DDD de ninguém aqui"
          description="O WhatsApp nem sempre mostra o número. Quando aparecer, o mapa se preenche."
          compact
        />
        <FraseDeCobertura cobertura={cobertura} />
      </>
    );
  }

  const ufsComDdd = uf ? data.porDdd.filter((d) => d.uf === uf.uf) : [];
  const topDdd = data.porDdd.slice(0, 5);
  const maiorDdd = topDdd[0]?.pessoas ?? 0;
  const maiorRegiao = data.porRegiao[0]?.pessoas ?? 0;

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="min-w-0">
          <MapaDoBrasil porUf={data.porUf} escolhida={uf?.uf ?? null} onEscolher={setUfEscolhida} />
          <div
            className="mx-auto mt-3 flex max-w-sm items-center justify-center gap-1 text-2xs text-muted-foreground"
            aria-hidden="true"
          >
            <span className="mr-1">Menos</span>
            {[1, 2, 3, 4, 5].map((lvl) => (
              <span key={lvl} className={cn('size-3 rounded-xs', NIVEL[lvl])} />
            ))}
            <span className="ml-1">Mais</span>
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          {uf ? (
            <div className="rounded-md bg-muted p-3" aria-live="polite">
              <p className="text-sm font-medium text-foreground">
                {uf.nome}
                {uf.posicao !== null ? (
                  <span className="font-normal text-muted-foreground"> · {uf.posicao}º estado</span>
                ) : null}
              </p>
              <p className="nums mt-1 text-2xl font-semibold text-foreground">
                {uf.pessoas > 0 ? pctTexto(uf.pct, uf.pessoas) : '0%'}
              </p>
              <p className="nums text-xs text-muted-foreground">
                {uf.pessoas === 0
                  ? 'Ninguém com DDD deste estado.'
                  : `${n(uf.pessoas)} ${uf.pessoas === 1 ? 'pessoa' : 'pessoas'}`}
                {ufsComDdd.length > 0
                  ? ` · ${ufsComDdd.map((d) => `${d.ddd} ${d.cidade}`).join(', ')}`
                  : ''}
              </p>
            </div>
          ) : null}

          <section aria-label="Principais DDDs">
            <h4 className="mb-2 text-xs font-medium text-muted-foreground">Principais DDDs</h4>
            <ol className="flex flex-col gap-2">
              {topDdd.map((d) => (
                <li key={d.ddd} className="min-w-0">
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="min-w-0 truncate text-foreground">
                      <span className="nums font-semibold">{d.ddd}</span> {d.cidade}
                      <span className="text-muted-foreground"> · {d.uf}</span>
                    </span>
                    <span className="nums shrink-0 text-muted-foreground">
                      {pctTexto(d.pct, d.pessoas)}
                    </span>
                  </div>
                  <Barra valor={d.pessoas} maior={maiorDdd} />
                </li>
              ))}
            </ol>
          </section>

          <section aria-label="Por região">
            <h4 className="mb-2 text-xs font-medium text-muted-foreground">Por região</h4>
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2">
              {data.porRegiao.map((r) => (
                <li key={r.regiao} className="min-w-0">
                  <div className="flex items-baseline justify-between gap-2 text-sm">
                    <span className="truncate text-foreground">{r.nome}</span>
                    <span className="nums shrink-0 text-muted-foreground">
                      {r.pessoas > 0 ? pctTexto(r.pct, r.pessoas) : '0%'}
                    </span>
                  </div>
                  <Barra valor={r.pessoas} maior={maiorRegiao} />
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>

      <FraseDeCobertura cobertura={cobertura} />
    </>
  );
}

type Uf = RegiaoData['porUf'][number];

function rotuloUf(u: Uf) {
  return `${u.nome}: ${n(u.pessoas)} ${u.pessoas === 1 ? 'pessoa' : 'pessoas'}, ${pctTexto(u.pct, u.pessoas)}`;
}

/**
 * O contorno de cada estado é o botão (teclado: Tab + Enter/Espaço). O escolhido é desenhado por
 * último para a borda não ficar escondida atrás do vizinho.
 */
function MapaDoBrasil({
  porUf,
  escolhida,
  onEscolher,
}: {
  porUf: readonly Uf[];
  escolhida: string | null;
  onEscolher: (uf: string) => void;
}) {
  const noMapa = porUf.filter((u) => ESTADOS[u.uf]);
  const ordem = [
    ...noMapa.filter((u) => u.uf !== escolhida),
    ...noMapa.filter((u) => u.uf === escolhida),
  ];
  const pequenas = PEQUENAS.map((sigla) => porUf.find((u) => u.uf === sigla)).filter((u): u is Uf =>
    Boolean(u),
  );

  return (
    <>
      <svg
        role="group"
        aria-label="Mapa do Brasil por estado"
        viewBox={`-6 -6 ${MAPA_LARGURA + 12} ${MAPA_ALTURA + 12}`}
        className="mx-auto block h-auto w-full max-w-sm"
      >
        {ordem.map((u) => {
          const e = ESTADOS[u.uf]!;
          const sel = u.uf === escolhida;
          return (
            <path
              key={u.uf}
              data-uf={u.uf}
              d={e.d}
              role="button"
              tabIndex={0}
              aria-pressed={sel}
              aria-label={rotuloUf(u)}
              onClick={() => onEscolher(u.uf)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault();
                  onEscolher(u.uf);
                }
              }}
              strokeLinejoin="round"
              className={cn(
                'cursor-pointer outline-none transition-[fill] hover:brightness-95',
                'focus-visible:stroke-ring',
                NIVEL_FILL[u.nivel],
                sel ? 'stroke-foreground [stroke-width:5]' : 'stroke-surface [stroke-width:2]',
              )}
            />
          );
        })}
        {noMapa
          .filter((u) => !(PEQUENAS as readonly string[]).includes(u.uf))
          .map((u) => {
            const e = ESTADOS[u.uf]!;
            return (
              <text
                key={u.uf}
                x={e.cx}
                y={e.cy}
                textAnchor="middle"
                aria-hidden="true"
                className={cn('pointer-events-none select-none', NIVEL_TINTA[u.nivel])}
              >
                <tspan x={e.cx} fontSize={30} className="font-semibold">
                  {u.uf}
                </tspan>
                {u.pessoas > 0 ? (
                  <tspan x={e.cx} dy={30} fontSize={24} className="nums">
                    {pctTexto(u.pct, u.pessoas)}
                  </tspan>
                ) : null}
              </text>
            );
          })}
      </svg>

      {/* Atalho para as UFs pequenas: o mesmo toque, sem precisar acertar o contorno. */}
      <div className="mx-auto mt-2 flex max-w-sm flex-wrap justify-center gap-1" aria-hidden="true">
        {pequenas.map((u) => (
          <button
            key={u.uf}
            type="button"
            tabIndex={-1}
            onClick={() => onEscolher(u.uf)}
            className={cn(
              'nums flex items-center gap-1 rounded-sm px-1.5 py-1 text-2xs leading-none',
              NIVEL[u.nivel],
              u.uf === escolhida && 'ring-2 ring-foreground ring-offset-1 ring-offset-surface',
            )}
          >
            <span className="font-semibold">{u.uf}</span>
            {u.pessoas > 0 ? <span>{pctTexto(u.pct, u.pessoas)}</span> : null}
          </button>
        ))}
      </div>
    </>
  );
}

/** Barra fina proporcional ao maior da lista — escala de leitura, não métrica nova. */
function Barra({ valor, maior }: { valor: number; maior: number }) {
  const largura = maior > 0 ? Math.max(valor > 0 ? 2 : 0, (valor / maior) * 100) : 0;
  return (
    <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden="true">
      <div className="h-full rounded-full bg-primary" style={{ width: `${largura}%` }} />
    </div>
  );
}

/** A base das porcentagens e quem ficou de fora, cada um com o seu nome. Nunca some do total. */
function FraseDeCobertura({ cobertura }: { cobertura: RegiaoData['cobertura'] }) {
  const partes = [
    `Baseado em ${n(cobertura.comTelefoneBr)} ${cobertura.comTelefoneBr === 1 ? 'pessoa' : 'pessoas'} com telefone do Brasil`,
  ];
  if (cobertura.semTelefone > 0) partes.push(`${n(cobertura.semTelefone)} sem telefone conhecido`);
  if (cobertura.estrangeiro > 0) partes.push(`${n(cobertura.estrangeiro)} de outros países`);
  if (cobertura.naoReconhecido > 0) {
    partes.push(`${n(cobertura.naoReconhecido)} com número não reconhecido`);
  }
  return (
    <p className="nums mt-4 text-xs text-muted-foreground">
      {partes.join('; ')}. O DDD mostra onde a linha foi registrada.
    </p>
  );
}
